import {doGet, doPost} from "./do-fetch"

/*
 * Topic sessions on the client (see code-changes/101-topic-sessions.md).
 *
 * A session is one attempt at a topic. The server hangs the live one on each leaf topic as
 * `topic.session` ({id, started_at, completed_at}) or null, and scopes `question.userAnswer` to
 * it — so everything below is derived from data the dashboard already has, with no extra request.
 *
 * The button a topic shows is a pure function of that session:
 *
 *   no session                      -> START
 *   session, no completed_at        -> RESUME
 *   session with completed_at       -> RESTART or REVIEW
 */

const TOPIC_SESSION_STATUS = {
	NOT_STARTED: 'NOT_STARTED',
	IN_PROGRESS: 'IN_PROGRESS',
	COMPLETED:   'COMPLETED',
}

const topicSessionStatus = (topic) => {
	const session = topic && topic.session
	if (!session) return TOPIC_SESSION_STATUS.NOT_STARTED
	return session.completed_at
		? TOPIC_SESSION_STATUS.COMPLETED
		: TOPIC_SESSION_STATUS.IN_PROGRESS
}

const isTopicSessionComplete = (topic) =>
	topicSessionStatus(topic) === TOPIC_SESSION_STATUS.COMPLETED

/*
 * The primary action for a topic. A completed topic offers Review alongside this one, which the
 * card renders as a second button — this returns the action that changes state.
 */
const topicActionLabel = (topic) => {
	switch (topicSessionStatus(topic)) {
		case TOPIC_SESSION_STATUS.COMPLETED:   return 'Restart'
		case TOPIC_SESSION_STATUS.IN_PROGRESS: return 'Resume'
		default:                               return 'Start'
	}
}

/*
 * Every leaf topic under a section (or a product), at any depth.
 */
const leafTopicsOf = (node) => {
	const children = (node && node.topics) || []
	if (!children.length) return node && node.questions ? [node] : []

	return children.reduce((all, child) => {
		const grandchildren = (child.topics || []).length
		return all.concat(grandchildren ? leafTopicsOf(child) : [child])
	}, [])
}

/*
 * Section status, from its topics. Sections offer START -> RESUME -> REVIEW only; there is no
 * Restart at section level, because a restart throws away an attempt and that decision belongs to
 * one topic at a time.
 *
 *   no topic has a session         -> NOT_STARTED  (Start)
 *   every topic is completed       -> COMPLETED    (Review)
 *   anything in between            -> IN_PROGRESS  (Resume)
 *
 * A section with no topics reads as NOT_STARTED rather than "all complete" — vacuous truth would
 * put a Review button on an empty section.
 */
const sectionSessionStatus = (section) => {
	const topics = leafTopicsOf(section)
	if (!topics.length) return TOPIC_SESSION_STATUS.NOT_STARTED

	const statuses = topics.map(topicSessionStatus)

	if (statuses.every(s => s === TOPIC_SESSION_STATUS.NOT_STARTED)) {
		return TOPIC_SESSION_STATUS.NOT_STARTED
	}
	if (statuses.every(s => s === TOPIC_SESSION_STATUS.COMPLETED)) {
		return TOPIC_SESSION_STATUS.COMPLETED
	}
	return TOPIC_SESSION_STATUS.IN_PROGRESS
}

const sectionActionLabel = (section) => {
	switch (sectionSessionStatus(section)) {
		case TOPIC_SESSION_STATUS.COMPLETED:   return 'Review'
		case TOPIC_SESSION_STATUS.IN_PROGRESS: return 'Resume'
		default:                               return 'Start'
	}
}

/*
 * The first topic in a section that still has work in it, or the first topic when everything is
 * done (Review goes to the first topic, as specified).
 */
const firstUnfinishedTopic = (section) => {
	const topics = leafTopicsOf(section)
	return topics.find(t => !isTopicSessionComplete(t)) || topics[0] || null
}

/*
 * What a section's one button does, and to which topic (110).
 *
 *   Start   nothing begun          -> the FIRST topic
 *   Resume  something in flight    -> the first topic actually IN PROGRESS, so Resume lands on the
 *                                     attempt already open rather than on an untouched topic that
 *                                     merely happens to come earlier in the list. Falls back to the
 *                                     first unstarted topic when every open attempt has been
 *                                     finished but the section as a whole has not.
 *   Review  everything complete    -> the FIRST topic, as specified
 *
 * The action kind is 'start' for both Start and Resume: the server treats them identically —
 * startTopicSession resumes a live session and leaves started_at alone — and the client opens the
 * first question without an answer either way. There is deliberately no restart here; discarding an
 * attempt is a per-topic decision.
 */
const sectionActionTarget = (section) => {
	const topics = leafTopicsOf(section)
	if (!topics.length) return null

	const status = sectionSessionStatus(section)

	if (status === TOPIC_SESSION_STATUS.COMPLETED) {
		return {topic: topics[0], kind: 'review'}
	}

	if (status === TOPIC_SESSION_STATUS.IN_PROGRESS) {
		const inFlight = topics.find(t => topicSessionStatus(t) === TOPIC_SESSION_STATUS.IN_PROGRESS)
		const unstarted = topics.find(t => topicSessionStatus(t) === TOPIC_SESSION_STATUS.NOT_STARTED)
		return {topic: inFlight || unstarted || topics[0], kind: 'start'}
	}

	return {topic: topics[0], kind: 'start'}
}

/*
 * A section's own counts, rolled up from its topics.
 *
 * scoreTopic already aggregates answeredCount / correctCount / questionCount onto a parent topic,
 * so this reads them rather than re-walking the tree — one source for the numbers the section row
 * and the topic rows both show.
 */
const sectionCounts = (section) => {
	const answered = (section && section.answeredCount) || 0
	const total = (section && section.questionCount) || 0
	const correct = (section && section.correctCount) || 0

	return {
		answered,
		total,
		correct,
		accuracy: answered > 0 ? Math.round((correct / answered) * 100) : 0,
		percent: total > 0 ? Math.round((answered / total) * 100) : 0,
	}
}

/* ---------- filtering (104) ---------- */

/*
 * The chips above the course content. One filter applies across every product and every section at
 * once, so a student can ask "what have I not finished?" of the whole kit rather than section by
 * section.
 */
const TOPIC_FILTERS = [
	{key: 'ALL',         label: 'All'},
	// in the order a topic passes through them: untouched, open, finished, then finished badly (182)
	{key: 'NOT_STARTED', label: 'Not Started'},
	{key: 'IN_PROGRESS', label: 'In Progress'},
	{key: 'COMPLETED',   label: 'Completed'},
	{key: 'NEEDS_WORK',  label: 'Needs Work'},
]

/*
 * "Needs Work" is a FINISHED topic that was not passed — the same 75% bar TopicScore congratulates
 * on and the daily-challenge ladder tops out at.
 *
 * Deliberately not "has any wrong answer": on a 20-question topic that would match almost
 * everything, and a filter that matches everything is not a filter. It is also not "unfinished" —
 * that is what In Progress is for.
 */
const NEEDS_WORK_BELOW = 75

/*
 * Below this a score reads red; between it and NEEDS_WORK_BELOW it reads amber (121).
 *
 * Two thresholds, two meanings, and they are not interchangeable: 75 is the PASS bar — it decides
 * the Needs Work badge and the filter chip — while 65 only grades how far off a failing score is.
 * Using NEEDS_WORK_BELOW for both is what silently deleted amber from the dashboard, because
 * `>= 75 ? green : >= 75 ? amber : red` can never reach its middle branch.
 */
const SCORE_AMBER_BELOW = 65

/*
 * The colour tier for a score: 'PASS' (>=75), 'NEAR' (65-74) or 'LOW' (<65).
 *
 * One implementation, because this ladder had drifted into four copies and three of them were
 * wrong. Returns a tier rather than a colour so it stays free of the theme.
 */
const scoreTier = (accuracy) => {
	if (accuracy >= NEEDS_WORK_BELOW) return 'PASS'
	if (accuracy >= SCORE_AMBER_BELOW) return 'NEAR'
	return 'LOW'
}

const topicAccuracy = (topic) => {
	const answered = (topic && topic.answeredCount) || 0
	const correct = (topic && topic.correctCount) || 0
	return answered > 0 ? Math.round((correct / answered) * 100) : 0
}

const topicMatchesFilter = (topic, filter) => {
	if (!filter || filter === 'ALL') return true

	const status = topicSessionStatus(topic)

	switch (filter) {
		case 'IN_PROGRESS':  return status === TOPIC_SESSION_STATUS.IN_PROGRESS
		case 'NOT_STARTED':  return status === TOPIC_SESSION_STATUS.NOT_STARTED
		case 'COMPLETED':    return status === TOPIC_SESSION_STATUS.COMPLETED
		case 'NEEDS_WORK':
			return status === TOPIC_SESSION_STATUS.COMPLETED &&
				topicAccuracy(topic) < NEEDS_WORK_BELOW
		default:             return true
	}
}

// does anything under this section (or product) survive the filter?
const hasMatchingTopic = (node, filter) =>
	leafTopicsOf(node).some(t => topicMatchesFilter(t, filter))

/*
 * How many topics across the whole kit each chip would show. Drives the disabled state, so a chip
 * that would empty the screen is visibly unavailable rather than silently unhelpful.
 */
const countTopicsByFilter = (products) => {
	const topics = (products || []).reduce((all, p) => all.concat(leafTopicsOf(p)), [])

	return TOPIC_FILTERS.reduce((counts, f) => {
		counts[f.key] = topics.filter(t => topicMatchesFilter(t, f.key)).length
		return counts
	}, {})
}

/* ---------- API ---------- */

const startTopicSession = async (prepKitId, topicId) => {
	const response = await doPost(`/mini/prepkit/${prepKitId}/topic/${topicId}/session`)
	if (!response.ok) throw new Error(`Could not start this topic (${response.status}).`)
	const data = await response.json()
	return data.session
}

const restartTopicSession = async (prepKitId, topicId) => {
	const response = await doPost(`/mini/prepkit/${prepKitId}/topic/${topicId}/session/restart`)
	if (!response.ok) throw new Error(`Could not restart this topic (${response.status}).`)
	const data = await response.json()
	return data.session
}

// {current, history} — each session carrying the answers inside its own window
const fetchTopicSessions = async (prepKitId, topicId) => {
	const response = await doGet(`/mini/prepkit/${prepKitId}/topic/${topicId}/sessions`)
	if (!response.ok) throw new Error(`Could not load this topic's history (${response.status}).`)
	return await response.json()
}

/*
 * Score one session's answers. Used for past attempts in the details drawer, where there is no
 * scored topic object to read — only the raw answers the session endpoint returned.
 */
const scoreSession = (session, questionCount) => {
	const answers = (session && session.answers) || []

	// one row per question: a session can hold a replaced answer as well as its replacement
	const latest = {}
	for (const a of answers) latest[a.question_id] = a

	const rows = Object.values(latest)
	const correct = rows.filter(a => a.is_correct).length

	return {
		answered: rows.length,
		correct,
		total: questionCount || rows.length,
		accuracy: rows.length ? Math.round((correct / rows.length) * 100) : 0,
	}
}

export {
	TOPIC_SESSION_STATUS,
	TOPIC_FILTERS,
	NEEDS_WORK_BELOW,
	SCORE_AMBER_BELOW,
	scoreTier,
	topicMatchesFilter,
	hasMatchingTopic,
	countTopicsByFilter,
	topicSessionStatus,
	isTopicSessionComplete,
	topicActionLabel,
	sectionSessionStatus,
	sectionActionLabel,
	leafTopicsOf,
	firstUnfinishedTopic,
	sectionActionTarget,
	sectionCounts,
	startTopicSession,
	restartTopicSession,
	fetchTopicSessions,
	scoreSession,
}
